'use client';

import React, { useState, useEffect, useRef, ChangeEvent, FormEvent } from 'react';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <article className={`card rounded-2xl p-6 ${className || ''}`}>{children}</article>
);

const Chip: React.FC<React.PropsWithChildren<{ href: string }>> = ({ href, children }) => (
  <a href={href} className="px-3 py-1 rounded-full hover:bg-white/25 text-sm" style={{ background: 'rgba(255,255,255,.14)' }}>{children}</a>
);

const PARTNERSHIP_DATA = [
  {
    name: 'Реферальный партнёр', cat: 'Реферал', payout: 'процент',
    perks: ['Комиссия до 10–15%', 'Быстрые выплаты', 'Материалы для промо']
  },
  {
    name: 'Технологический партнёр', cat: 'Технологии', payout: 'фикс',
    perks: ['Интеграции и вебхуки', 'Совместный маркетинг', 'Co-selling']
  },
  {
    name: 'MSP / Субподряд', cat: 'MSP', payout: 'фикс',
    perks: ['SLA и объёмы', 'Единые стандарты качества', 'Доступ к заявкам']
  },
  {
    name: 'Агентство / Реселлер', cat: 'Агентство', payout: 'процент',
    perks: ['Маржа до 15%', 'Белая метка/ко-брендинг', 'Единый дашборд']
  },
  {
    name: 'Образовательный партнёр', cat: 'Образование', payout: 'фикс',
    perks: ['Гостевые лекции', 'Стажировки', 'Совместные мероприятия']
  },
];

export default function PartnershipPage() {
  const [filters, setFilters] = useState({ query: '', category: '', payout: '' });
  const [formValues, setFormValues] = useState({ type: '', company: '', contact: '', email: '', message: '' });
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFilters(prev => ({ ...prev, [id]: value }));
  };

  const handleFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isConsentChecked) {
      setStatus('Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    setSubmitting(true);
    setStatus("Отправка данных...");

    const endpoint = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/partnership-requests`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data: {
            formName: 'Сотрудничество',
            ...formValues,
          },
        }),
      });

      if (response.ok) {
        setStatus("Ваша заявка успешно отправлена!");
        setFormValues({ type: '', company: '', contact: '', email: '', message: '' });
        setIsConsentChecked(false);
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || "Произошла неизвестная ошибка.";
        setStatus(`Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      setStatus("Ошибка сети. Пожалуйста, попробуйте снова.");
      console.error("Ошибка:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredItems = PARTNERSHIP_DATA.filter(x => {
    const okQ = !filters.query || x.name.toLowerCase().includes(filters.query.toLowerCase());
    const okC = !filters.category || x.cat === filters.category;
    const okP = !filters.payout || x.payout === filters.payout;
    return okQ && okC && okP;
  });

  const countersRef = useRef<NodeListOf<Element> | null>(null);
  useEffect(() => {
    countersRef.current = document.querySelectorAll('.counter');
    const animate = (el: Element) => {
      const to = Number(el.getAttribute('data-to'));
      const duration = 900;
      const start = performance.now();
      const step = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        (el as HTMLElement).textContent = Math.floor(to * progress).toString();
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    countersRef.current.forEach(counter => observer.observe(counter));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <Chip href="#types">Форматы партнёрства</Chip>
            <Chip href="#tiers">Уровни программы</Chip>
            <Chip href="#process">Как начать</Chip>
            <Chip href="#case">Кейс</Chip>
            <Chip href="#faq">FAQ</Chip>
            <Chip href="#apply">Оставить заявку</Chip>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Сотрудничество с INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Реферальная программа, технологические интеграции, MSP/субподряд, образовательные и агентские партнёрства.
            Прозрачные условия, быстрые выплаты и единые соглашения об уровне сервиса (SLA).
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#apply" className="btn btn-accent">Стать партнёром</a>
            <a href="#types" className="btn btn-ghost">Смотреть форматы</a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="metrics" className="scroll-mt-32">
          <div className="grid gap-6 md:grid-cols-4">
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="42">0</div>
              <div className="text-xs text-gray-300 mt-1">активных партнёров</div>
            </Card>
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="14">0</div>
              <div className="text-xs text-gray-300 mt-1">дней до выплаты</div>
            </Card>
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="27">0</div>
              <div className="text-xs text-gray-300 mt-1">интеграций и коннекторов</div>
            </Card>
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="92">0</div>
              <div className="text-xs text-gray-300 mt-1">Оценка удовлетворённости</div>
            </Card>
          </div>
        </section>

        <section id="types" className="scroll-mt-32">
          <div className="flex flex-wrap items-end gap-4 justify-between">
            <h2 className="text-3xl md:text-4xl font-bold">Форматы партнёрства</h2>
            <div className="grid gap-3 md:grid-cols-3">
              <input id="query" value={filters.query} onChange={handleFilterChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="Поиск по форматам…" />
              <select id="category" value={filters.category} onChange={handleFilterChange} className="rounded-xl px-4 py-3 text-gray-900">
                <option value="">Категория: все</option>
                <option value="Реферал">Реферал</option>
                <option value="Технологии">Технологии</option>
                <option value="MSP">MSP/Субподряд</option>
                <option value="Агентство">Агентство/Реселлер</option>
                <option value="Образование">Образование</option>
              </select>
              <select id="payout" value={filters.payout} onChange={handleFilterChange} className="rounded-xl px-4 py-3 text-gray-900">
                <option value="">Выплата</option>
                <option value="фикс">Фикс</option>
                <option value="процент">Процент</option>
              </select>
            </div>
          </div>
          <div id="list" className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <Card key={index} className="flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-xl font-semibold">{item.name}</h3>
                      <div className="mt-1 flex gap-2 text-xs">
                        <span className="tag">{item.cat}</span>
                        <span className="tag">{item.payout === 'фикс' ? 'Фикс' : 'Процент'}</span>
                      </div>
                    </div>
                    <a href="#apply" className="btn btn-ghost text-sm">Подать заявку</a>
                  </div>
                  <ul className="text-sm text-gray-200 mt-3 space-y-2">
                    {item.perks.map((perk, perkIndex) => (
                      <li key={perkIndex}>• {perk}</li>
                    ))}
                  </ul>
                </Card>
              ))
            ) : (
              <p className="col-span-3 text-gray-300">Ничего не найдено — измените фильтры.</p>
            )}
          </div>
        </section>

        <section id="tiers" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Уровни программы</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <div className="tag mb-2">Тариф</div>
              <h3 className="text-xl font-semibold">Silver</h3>
              <p className="text-sm text-gray-200 mt-1">Старт для новых партнёров</p>
              <ul className="text-sm text-gray-200 mt-3 space-y-2">
                <li>• Комиссия до 10% за реферал</li>
                <li>• Выплата в течение 14 дней</li>
                <li>• Доступ к шаблонам и материалам</li>
              </ul>
            </Card>
            <Card>
              <div className="tag mb-2">Тариф</div>
              <h3 className="text-xl font-semibold">Gold</h3>
              <p className="text-sm text-gray-200 mt-1">Для активных партнёров</p>
              <ul className="text-sm text-gray-200 mt-3 space-y-2">
                <li>• Комиссия до 15% / прибыль реселлера до 15%</li>
                <li>• Совместные вебинары и кейсы</li>
                <li>• Приоритетная поддержка</li>
              </ul>
            </Card>
            <Card>
              <div className="tag mb-2">Тариф</div>
              <h3 className="text-xl font-semibold">Platinum</h3>
              <p className="text-sm text-gray-200 mt-1">Стратегические партнёрства</p>
              <ul className="text-sm text-gray-200 mt-3 space-y-2">
                <li>• Индивидуальные условия и SLA</li>
                <li>• Ко-брендинг, общий роадмап</li>
                <li>• Расширенная BI-отчётность</li>
              </ul>
            </Card>
          </div>
        </section>

        <section id="process" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Как начать сотрудничество</h2>
          <div className="grid gap-6 md:grid-cols-4">
            <Card><h3 className="text-xl font-semibold">1. Заявка</h3><p className="text-sm text-gray-200 mt-1">Отправьте форму — вернёмся в течение 24 часов.</p></Card>
            <Card><h3 className="text-xl font-semibold">2. Колл</h3><p className="text-sm text-gray-200 mt-1">Уточняем формат, KPI и интеграции.</p></Card>
            <Card><h3 className="text-xl font-semibold">3. Договор</h3><p className="text-sm text-gray-200 mt-1">Фиксируем SLA, комиссию и процесс выплат.</p></Card>
            <Card><h3 className="text-xl font-semibold">4. Запуск</h3><p className="text-sm text-gray-200 mt-1">Бриф/тех.настройки, общий чат и отчётность.</p></Card>
          </div>
        </section>

        <section id="case" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Кейс партнёрства</h2>
          <div className="simple-card grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="text-xl font-semibold">RPO + MSP для электронной коммерции</h3>
              <p className="text-sm text-gray-200 mt-2">
                Партнёр-платформа привела клиента, мы закрыли 120+ позиций за квартал.
                Совместные вебинары, общий дашборд KPI и еженедельные ревью.
              </p>
              <ul className="text-sm text-gray-200 mt-3 space-y-2">
                <li>• Снижение стоимости найма (CPH) −18 %, срок замены (SLA) 24 ч</li>
                <li>• Удовлетворённость клиентов (NPS) +22 п.п.</li>
                <li>• Выплаты партнёру — 2 раза в месяц</li>
              </ul>
            </div>
            <img className="rounded-xl w-full h-64 object-cover" src="https://www.stephens-scown.co.uk/app/uploads/2017/05/jigsaw-499147276.jpg" alt="" />
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Как рассчитывается комиссия?</summary>
              <p className="mt-2 text-sm text-gray-200">Фикс или процент от нашей комиссии за закрытие — зависит от формата (реферал/реселлер/MSP). Уточняем в договоре.</p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Какие интеграции доступны?</summary>
              <p className="mt-2 text-sm text-gray-200">Яндекс Почта, Яндекс Календарь, Яндекс Диск, Flyvi, коннекторы Huntflow/Битрикс24/amoCRM, вебхуки через ApixDrive/Albato.</p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Сроки выплат?</summary>
              <p className="mt-2 text-sm text-gray-200">Обычно до 14 дней после подтверждения результата по KPI.</p>
            </details>
          </div>
        </section>

        <section id="apply" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Оставить заявку на партнёрство</h2>
          <form className="simple-card grid gap-4 md:grid-cols-2" onSubmit={handleFormSubmit}>
            <select name="type" value={formValues.type} onChange={handleFormChange} className="rounded-xl px-4 py-3 text-gray-900" required>
              <option value="">Тип партнёрства*</option>
              <option>Реферал</option>
              <option>Технологии</option>
              <option>MSP/Субподряд</option>
              <option>Агентство/Реселлер</option>
              <option>Образование</option>
            </select>
            <input name="company" value={formValues.company} onChange={handleFormChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="Компания*" required />
            <input name="contact" value={formValues.contact} onChange={handleFormChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="Контактное лицо*" required />
            <input name="email" value={formValues.email} onChange={handleFormChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="E-mail*" type="email" required />
            <textarea name="message" value={formValues.message} onChange={handleFormChange} className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2" rows={4} placeholder="Кратко опишите предложение / целевой кейс"></textarea>
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-accent md:col-span-2 disabled:opacity-50"
            >
              {submitting ? "Отправка..." : "Отправить"}
            </button>
            <CustomCheckbox
              name="consent"
              isChecked={isConsentChecked}
              onChange={handleConsentChange}
              link="/privacy"
              textColorClass="text-gray-400"
              linkColorClass="text-[#FF7C22]"
              className="md:col-span-2"
            />
            {status && (
              <div className={`mt-2 text-center md:col-span-2 text-sm ${status.startsWith("Ошибка") ? "text-red-400" : "text-green-300"}`}>
                {status}
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}